import VoucherPlus from "@/app/assets/icons/voucherplus";
import Image from "next/image";
import React from "react";
import Link from "next/link";
import AmazonVoucher from "@/app/assets/images/amazon-voucher.png";
import { formatIndianCurrency } from "@/app/utils/helper";

type ReferVoucherProps = {
    voucherAmt?: number;
    isLoading?: boolean;
    voucherText?: string;
    hideTransactionBenefit?: boolean;
};

const ReferVoucher: React.FC<ReferVoucherProps> = ({ voucherAmt = 0, isLoading, voucherText = "On your referral's first transaction only", hideTransactionBenefit }) => {

    if (isLoading) {
        return (
            <div className="flex flex-col md:px-0 animate-pulse w-full md:w-auto">
                <div className="relative basis-1/3 rounded-sm border-2 border-dashed border-neutral-200 bg-neutral-100">
                    <div className="mr-6">
                        <div className="flex items-center justify-center gap-s8 p-s9">
                            <div className="h-10 w-20 rounded bg-neutral-200" />
                            <div className="h-14 w-24 rounded bg-neutral-200" />
                            <div className="flex-1">
                                <div className="h-5 w-32 rounded bg-neutral-200 mb-2" />
                                <div className="h-4 w-40 rounded bg-neutral-200" />
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center pb-s15 pl-s10 pt-s10 md:pl-s11">
                        <div className="h-10 w-10 rounded-full bg-neutral-200" />
                        <div className="absolute bottom-[-50px] left-[3.5rem] m-2 rotate-3 rounded-md bg-white shadow-inner outline-none ring-0 sm:right-6">
                            <div className="rounded-md border-2 border-dashed border-neutral-100">
                                <div className="flex justify-between gap-3 p-s9">
                                    <div>
                                        <div className="h-8 w-20 rounded bg-neutral-200 mb-2" />
                                        <div className="h-5 w-28 rounded bg-neutral-200" />
                                    </div>
                                    <div className="h-[66px] w-[42px] rounded bg-neutral-200" />
                                </div>
                                <div className="border-t border-dashed border-neutral-200 px-s9 py-s5">
                                    <div className="h-4 w-40 rounded bg-neutral-200" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Mobile only skeleton for the policy link */}
                <p className="block pt-s17 text-center text-sm leading-[19.2px] text-neutral-200 md:hidden">
                    <span className="h-4 w-40 mx-auto block rounded bg-neutral-200 mb-2" />
                    <span className="h-4 w-28 mx-auto block rounded bg-neutral-200" />
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col md:px-0">
            <div className="relative basis-1/3 rounded-sm border-2 border-dashed border-neutral-200">
                <div className={`mr-6 ${hideTransactionBenefit ? "invisible" : ""}`}>
                        <div className="flex items-center justify-center gap-s8 p-s9">
                            <div className="text-4xl font-semibold leading-[67px] tracking-[-1.92px] text-neutral-300 line-through">
                                0.25%
                            </div>
                            <div className="text-6xl font-semibold leading-[67px] tracking-[-1.92px] text-orange-500">
                                1%
                            </div>
                            <div className="flex-1">
                                <div className="text-md font-semibold leading-[22px] text-neutral-900">
                                    Transaction Benefit
                                </div>
                                <div className="text-xs font-semibold leading-[17px] text-neutral-500">
                                    On every transaction for 1 year
                                </div>
                            </div>
                        </div>
                    </div>
                <div className="flex items-center pb-s15 pl-s10 pt-s10 md:pl-s11">
                    <VoucherPlus />
                    <div className="absolute bottom-[-50px] left-[3.5rem] m-2 rotate-3 rounded-md bg-white shadow-inner outline-none ring-0 sm:right-6">
                        <div className="rounded-md border-2 border-dashed border-neutral-100">
                            <div className="flex justify-between gap-3 p-s9">
                                <div>
                                    <div className="text-4xl font-semibold leading-[44px] tracking-[-1.28px] text-neutral-500">
                                        {formatIndianCurrency(voucherAmt)}
                                    </div>
                                    <div className="text-md font-semibold leading-[22px] text-neutral-500">
                                        Amazon Pay Voucher
                                    </div>
                                </div>
                                <Image
                                    src={AmazonVoucher}
                                    alt="Voucher"
                                    max-height={66}
                                    max-width={42}
                                />
                            </div>
                            <div className="border-t border-dashed border-neutral-200 px-s9 py-s5 text-xs leading-[16px] text-neutral-500">
                                {voucherText}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="block pt-s17 text-center text-sm leading-[19.2px] text-neutral-500 md:hidden">
                Learn more about our{" "}
                <Link href={`${process.env.ICM_URL}/terms-of-use/`} className="underline">
                    referral policy.
                </Link>
            </p>
        </div>
    );
};

export default ReferVoucher;
